import React, { useEffect, useRef, useState } from 'react';
import { Col, Row, getItemWithExpiration } from '../../lib/utils';
import { colors } from '../../lib/colors';
import { Subtitle1Bold, Body2Regular, CaptionLight } from '../../lib/font';
import ColorSquareButton from '../../components/Button/ColorSquareButton';
import Modal from '../../components/modal/Modal';
import GroupSessionCreateModal from '../../components/GroupSession/GroupSessionCreateModal';
import GroupCategoryField, { isCustomCategory } from '../../components/GroupSession/GroupCategoryField';
import GroupRuleFields, { RuleFieldsValue, commitRuleFields, diffRuleFields, initRuleFields, rememberSavedRules, reseedRuleFields, withSavedRules } from '../../components/GroupSession/GroupRuleFields';
import { useReservationRules } from '@/features/reservationRules/hooks';
import { useTemplates, useDeleteTemplate, useUpdateTemplate } from '@/features/groupSession/hooks';
import { useTickets } from '@/features/groupTicket/hooks';
import { usePublicMemberList } from '@/features/publicMember/hooks';
import { GroupClassTemplate } from '@/features/groupSession/types';

const CLASS_TYPE_COLORS: Record<string, { bg: string; color: string; label: string; bar: string }> = {
    GROUP: { bg: '#ECFDF5', color: '#065F46', label: '그룹', bar: '#10B981' },
    PRIVATE_1ON2: { bg: '#EFF6FF', color: '#1E40AF', label: '1:2', bar: '#3B82F6' },
    PRIVATE_1ON3: { bg: '#F5F3FF', color: '#5B21B6', label: '1:3', bar: '#8B5CF6' },
};

const inputStyle: React.CSSProperties = {
    border: '1px solid #E1E4E6', borderRadius: 8, padding: '8px 12px', fontSize: 14, width: '100%', outline: 'none',
};

const GroupClassManagePage = ({ createAlert, embedded = false }: { createAlert: any; embedded?: boolean }) => {
    const [authObject] = useState(getItemWithExpiration('authObject'));
    const centerId = authObject?.centerId;

    const { data: templates = [], isLoading, dataUpdatedAt: templatesUpdatedAt } = useTemplates({ centerId });
    const { data: centerRules } = useReservationRules({ centerId });
    // isActive='false' 플래그로 비활성 포함 전체 조회 (ClassPassPage와 동일 패턴)
    const { data: ticketProducts = [] } = useTickets({ centerId, params: { isActive: 'false' } });
    const deleteMutation = useDeleteTemplate();
    const updateMutation = useUpdateTemplate();

    // 강사 목록
    const { data: memberData } = usePublicMemberList({ centerId, memberType: 'all', limit: 200 });
    const coachList = (() => {
        const map = new Map<string, string>();
        (memberData?.results ?? []).forEach((m: any) => {
            if (m.coachId && !map.has(m.coachId)) map.set(m.coachId, m.coachName || m.coachId);
        });
        return Array.from(map.entries()).map(([code, value]) => ({ code, value }));
    })();

    const [isCreateModal, setIsCreateModal] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState<GroupClassTemplate | null>(null);
    const [expandedId, setExpandedId] = useState<string | null>(null);
    // 저장 응답이 올 때 지금 펼친 수업이 그 수업인지 — 저장 중 다른 수업을 펼치면 그 편집 상태를 덮으면 안 된다
    const expandedIdRef = useRef<string | null>(null);
    expandedIdRef.current = expandedId;
    const [showGuide, setShowGuide] = useState(() => localStorage.getItem('guide_groupClass') !== 'hidden');
    const [isDirty, setIsDirty] = useState(false);
    // 편집할 때마다 올린다 — 저장 응답이 왔을 때 그사이 더 고친 게 있는지 가린다
    const editVersionRef = useRef(0);
    const markDirty = () => {
        editVersionRef.current += 1;
        setIsDirty(true);
    };

    // 수정용 state
    const [editTitle, setEditTitle] = useState('');
    const [editCategory, setEditCategory] = useState('');
    const [editCategoryCustom, setEditCategoryCustom] = useState(false);
    const [editCoachId, setEditCoachId] = useState('');
    const [editMaxCapacity, setEditMaxCapacity] = useState(6);
    const [editMinCapacity, setEditMinCapacity] = useState(0);
    const [editWaitlistLimit, setEditWaitlistLimit] = useState(0);
    const [editRoomId, setEditRoomId] = useState('');
    const [editTicketIds, setEditTicketIds] = useState<string[]>([]);
    // 허용 수강권 변경을 예정된(미래) 수업에도 함께 적용할지 (기본 ON — 관리자 기대에 맞춤)
    const [applyToFuture, setApplyToFuture] = useState(true);
    const [editRules, setEditRules] = useState<RuleFieldsValue>(() => initRuleFields({}));
    // 센터 설정이 편집을 연 뒤에 오면, 임시 기본값으로 채웠던 칸을 센터 값으로 다시 채운다
    useEffect(() => {
        if (centerRules) setEditRules((prev) => reseedRuleFields(prev, centerRules));
    }, [centerRules]);

    // 백엔드가 allowedTicketIds를 populated 객체로 내려줄 수 있어 _id로 정규화
    const normalizeTicketIds = (raw: any): string[] =>
        ((raw ?? []) as any[])
            .map((x) => (typeof x === 'object' ? x?._id : x))
            .filter(Boolean) as string[];

    const startEdit = (c: GroupClassTemplate) => {
        // 저장 중에 접었다 펼치면 아직 옛 목록 값으로 다시 채워져, 다음 저장 때 방금 바꾼 규칙이 되돌아간다
        if (updateMutation.isLoading) return;
        if (expandedId && expandedId !== c._id && isDirty) {
            const ok = window.confirm('저장하지 않은 변경사항이 있습니다.\n다른 수업으로 이동하면 변경사항이 사라집니다.\n계속하시겠습니까?');
            if (!ok) return;
        }
        setIsDirty(false);
        setExpandedId(expandedId === c._id ? null : c._id);
        setEditTitle(c.title || '');
        setEditCategory(c.category || '');
        setEditCategoryCustom(isCustomCategory(c.category));
        setEditCoachId(c.defaultCoachId || '');
        setEditMaxCapacity(c.maxCapacity);
        setEditMinCapacity(c.minCapacity);
        setEditWaitlistLimit(c.waitlistLimit);
        setEditRoomId(c.roomId || '');
        setEditTicketIds(normalizeTicketIds(c.allowedTicketIds));
        setApplyToFuture(true);
        setEditRules(initRuleFields(withSavedRules(`template:${c._id}`, c, templatesUpdatedAt), centerRules));
    };

    const handleSave = async (c: GroupClassTemplate) => {
        const templateId = c._id;
        const nextTitle = editTitle.trim();
        const nextCategory = editCategory.trim();
        if (!nextTitle) {
            createAlert('입력 오류', '수업명을 입력해주세요.', true);
            return;
        }
        if (!nextCategory) {
            createAlert('입력 오류', '카테고리를 입력해주세요.', true);
            return;
        }
        if (editMinCapacity > editMaxCapacity) {
            createAlert('입력 오류', '최소 인원은 최대 정원보다 클 수 없습니다.', true);
            return;
        }
        if (editMaxCapacity < 1) {
            createAlert('입력 오류', '최대 정원은 1명 이상이어야 합니다.', true);
            return;
        }
        // 바뀐 경우에만 보낸다 — 서버가 수업명·카테고리 변경을 예정 회차에도 반영하므로 불필요한 갱신을 막는다
        const titleChanged = nextTitle !== (c.title ?? '');
        const categoryChanged = nextCategory !== (c.category ?? '');
        // 바뀐 규칙만 보낸다 — 서버가 예정 회차에도 반영하므로 손대지 않은 규칙으로 회차별 설정을 덮지 않게
        const changedRules = diffRuleFields(editRules);
        const rulesChanged = Object.keys(changedRules).length > 0;
        const versionAtSave = editVersionRef.current;
        try {
            const res = await updateMutation.mutateAsync({
                centerId, templateId,
                body: {
                    ...(titleChanged ? { title: nextTitle } : {}),
                    ...(categoryChanged ? { category: nextCategory } : {}),
                    // 규칙을 바꿨을 때만 예정 회차 반영을 요청한다(구서버는 이 키를 모르면 400 이므로 그때만 붙인다)
                    ...(rulesChanged ? { ...changedRules, applyRulesToFutureSessions: true } : {}),
                    defaultCoachId: editCoachId || undefined,
                    maxCapacity: editMaxCapacity,
                    minCapacity: editMinCapacity,
                    waitlistLimit: editWaitlistLimit,
                    roomId: editRoomId || undefined,
                    allowedTicketIds: editTicketIds,
                    applyAllowedTicketsToFutureSessions: applyToFuture,
                },
            });
            rememberSavedRules(`template:${templateId}`, changedRules);
            if (expandedIdRef.current === templateId) {
                // 저장 중에 더 고친 내용이 있으면 '저장 필요' 표시를 남긴다(그 변경은 아직 서버에 없다)
                if (editVersionRef.current === versionAtSave) setIsDirty(false);
                const saved = editRules;
                setEditRules((prev) => commitRuleFields(prev, saved));
            }
            const applied = (res as any)?.data?.appliedFutureSessionCount ?? 0;
            // 구서버는 이 필드를 주지 않는다 — 없으면 안내를 붙이지 않는다
            const renamedRaw = (res as any)?.data?.renamedFutureSessionCount;
            const renamed = typeof renamedRaw === 'number' && renamedRaw > 0 ? renamedRaw : 0;
            const lines = ['수업이 수정되었습니다.'];
            if (applied > 0) lines.push(`예정된 수업 ${applied}개에도 신청 가능 수강권이 적용되었습니다.`);
            if (renamed > 0) lines.push(`예정된 수업 ${renamed}개의 수업명·카테고리도 함께 바뀌었습니다.`);
            const rulesApplied = (res as any)?.data?.rulesAppliedFutureSessionCount;
            if (typeof rulesApplied === 'number' && rulesApplied > 0) lines.push(`예정된 수업 ${rulesApplied}개의 예약 규칙도 함께 바뀌었습니다.`);
            createAlert('수정 완료', lines.join('\n'), false);
        } catch {
            createAlert('수정 실패', '수업 수정에 실패했습니다.', true);
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        try {
            await deleteMutation.mutateAsync({ centerId, templateId: deleteTarget._id });
            createAlert('삭제 완료', '수업이 삭제되었습니다.', false);
            setDeleteTarget(null);
            if (expandedId === deleteTarget._id) setExpandedId(null);
        } catch {
            createAlert('삭제 실패', '수업 삭제에 실패했습니다.', true);
        }
    };

    const outerStyle = embedded
        ? { width: '100%' }
        : { width: '100%', height: '100%', backgroundColor: '#F8FAFC', marginTop: 64, overflow: 'auto' as const };
    const innerStyle = embedded
        ? { width: '100%' }
        : { width: '100%', maxWidth: 1200, margin: '0 auto', padding: '40px 48px 80px' };

    return (
        <Col style={outerStyle}>
            <Col style={innerStyle}>
                <Row style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                    <Col>
                        <Row style={{ alignItems: 'center', gap: 8 }}>
                            <Subtitle1Bold>그룹수업 관리</Subtitle1Bold>
                            {!showGuide && (
                                <button onClick={() => { setShowGuide(true); localStorage.removeItem('guide_groupClass'); }}
                                    style={{ width: 22, height: 22, borderRadius: '50%', border: '1.5px solid #6EE7B7', backgroundColor: '#ECFDF5', color: '#059669', fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>?</button>
                            )}
                        </Row>
                        <CaptionLight color={colors.LAVEL_1} style={{ marginTop: 4 }}>
                            수업을 미리 등록해두면 캘린더에서 빠르게 생성할 수 있습니다
                        </CaptionLight>
                    </Col>
                    <ColorSquareButton text="+ 새 수업 등록" size="small" callBack={() => setIsCreateModal(true)} />
                </Row>

                {showGuide && (
                    <Col style={{ marginTop: 16, padding: '18px 22px', backgroundColor: '#ECFDF5', borderRadius: 12, border: '1px solid #A7F3D0', position: 'relative' }}>
                        <button onClick={() => { setShowGuide(false); localStorage.setItem('guide_groupClass', 'hidden'); }}
                            style={{ position: 'absolute', top: 10, right: 14, background: 'none', border: 'none', fontSize: 16, color: '#6EE7B7', cursor: 'pointer' }}>✕</button>
                        <span style={{ fontSize: 14, fontWeight: 700, color: '#065F46', marginBottom: 6 }}>그룹수업 관리란?</span>
                        <Col style={{ gap: 3, fontSize: 13, color: '#065F46', lineHeight: 1.7 }}>
                            <span><b>1:1 PT를 제외한</b> 그룹 수업, 프라이빗(1:2, 1:3) 수업을 등록하고 설정하는 페이지입니다.</span>
                            <span>1:1 PT 수업은 기존 스케줄 관리에서 운영되며, 이 페이지는 <b>여러 명이 함께 참여하는 수업</b>을 관리합니다.</span>
                        </Col>
                        <Row style={{ gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
                            <span style={{ fontSize: 12, padding: '3px 10px', borderRadius: 6, backgroundColor: '#D1FAE5', color: '#065F46' }}>수업 등록</span>
                            <span style={{ fontSize: 12, color: '#6B7280' }}>→</span>
                            <span style={{ fontSize: 12, padding: '3px 10px', borderRadius: 6, backgroundColor: '#D1FAE5', color: '#065F46' }}>수강권 연결</span>
                            <span style={{ fontSize: 12, color: '#6B7280' }}>→</span>
                            <span style={{ fontSize: 12, padding: '3px 10px', borderRadius: 6, backgroundColor: '#D1FAE5', color: '#065F46' }}>캘린더에서 일정 생성</span>
                        </Row>
                    </Col>
                )}

                <Col style={{ marginTop: 32, gap: 12 }}>
                    {isLoading ? (
                        <Col style={{ alignItems: 'center', padding: '40px 0', gap: 12 }}>
                            <div style={{ width: 28, height: 28, border: '3px solid #3B82F6', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                            <CaptionLight color={colors.LAVEL_1}>수업 목록을 불러오는 중...</CaptionLight>
                        </Col>
                    ) : templates.length === 0 ? (
                        <Col style={{ alignItems: 'center', padding: '60px 0', gap: 16 }}>
                            <span style={{ fontSize: 40 }}>📋</span>
                            <span style={{ fontSize: 16, fontWeight: 600, color: '#374151' }}>등록된 수업이 없습니다</span>
                            <span style={{ fontSize: 14, color: '#9CA3AF' }}>수업을 등록하면 캘린더에서 빠르게 생성할 수 있습니다</span>
                            <button onClick={() => setIsCreateModal(true)}
                                style={{ padding: '12px 28px', borderRadius: 8, fontSize: 14, fontWeight: 600, backgroundColor: '#3B82F6', color: 'white', border: 'none', cursor: 'pointer' }}>
                                + 첫 수업 등록하기
                            </button>
                        </Col>
                    ) : (
                        (templates as GroupClassTemplate[]).map((c) => {
                            const typeCfg = CLASS_TYPE_COLORS[c.classType] || CLASS_TYPE_COLORS.GROUP;
                            const isExpanded = expandedId === c._id;
                            return (
                                <Col key={c._id} style={{ backgroundColor: 'white', borderRadius: 12, border: isExpanded ? `2px solid ${typeCfg.bar}` : '1px solid #E5E7EB', overflow: 'hidden' }}>
                                    {/* 헤더 (항상 보임) */}
                                    <Row style={{ padding: '16px 24px', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
                                        onClick={() => startEdit(c)}>
                                        <Row style={{ alignItems: 'center', gap: 16 }}>
                                            <div style={{ width: 4, height: 40, borderRadius: 2, backgroundColor: typeCfg.bar, flexShrink: 0 }} />
                                            <Col>
                                                <Row style={{ alignItems: 'center', gap: 8 }}>
                                                    <span style={{ fontSize: 16, fontWeight: 600, color: '#111827' }}>{c.title}</span>
                                                    <span style={{ fontSize: 12, fontWeight: 600, padding: '2px 8px', borderRadius: 4, backgroundColor: typeCfg.bg, color: typeCfg.color }}>{typeCfg.label}</span>
                                                </Row>
                                                <Row style={{ gap: 10, marginTop: 4 }}>
                                                    <span style={{ fontSize: 13, color: '#6B7280' }}>{c.category}</span>
                                                    {c.defaultCoachName && <span style={{ fontSize: 13, color: '#6B7280' }}>강사: {c.defaultCoachName}</span>}
                                                    <span style={{ fontSize: 13, color: '#6B7280' }}>정원 {c.maxCapacity}명</span>
                                                    {c.roomId && <span style={{ fontSize: 13, color: '#6B7280' }}>{c.roomId}</span>}
                                                </Row>
                                            </Col>
                                        </Row>
                                        <Row style={{ alignItems: 'center', gap: 8, flexShrink: 0 }}>
                                            {isExpanded && isDirty && (
                                                <span style={{ fontSize: 11, color: '#F59E0B', fontWeight: 600, padding: '2px 8px', borderRadius: 4, backgroundColor: '#FFFBEB', border: '1px solid #FDE68A' }}>
                                                    ● 저장 필요
                                                </span>
                                            )}
                                            {c.allowedTicketNames && c.allowedTicketNames.length > 0 && (
                                                <span style={{ fontSize: 12, color: '#3B82F6' }}>수강권 {c.allowedTicketNames.length}개</span>
                                            )}
                                            <span style={{ fontSize: 12, color: '#9CA3AF', transform: isExpanded ? 'rotate(180deg)' : '', transition: 'transform 0.15s' }}>▼</span>
                                        </Row>
                                    </Row>

                                    {/* 상세 (펼침) */}
                                    {isExpanded && (
                                        <Col style={{ padding: '0 24px 20px', gap: 16, borderTop: '1px solid #F3F4F6', paddingTop: 16 }}>
                                            {/* 수업명 / 카테고리 */}
                                            <Row style={{ gap: 12, alignItems: 'flex-start' }}>
                                                <Col style={{ flex: 2, gap: 4 }}>
                                                    <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>수업명</span>
                                                    <input value={editTitle} maxLength={100} onChange={(e) => { setEditTitle(e.target.value); markDirty(); }} placeholder="수업명" style={{ ...inputStyle, borderColor: !editTitle.trim() ? '#EF4444' : undefined }} />
                                                    {!editTitle.trim() && <span style={{ fontSize: 11, color: '#EF4444' }}>수업명을 입력해주세요</span>}
                                                </Col>
                                                <Col style={{ flex: 1, gap: 4 }}>
                                                    <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>카테고리</span>
                                                    <GroupCategoryField
                                                        value={editCategory}
                                                        isCustom={editCategoryCustom}
                                                        onChange={({ value, isCustom }) => { setEditCategory(value); setEditCategoryCustom(isCustom); markDirty(); }}
                                                        selectStyle={inputStyle}
                                                        inputStyle={inputStyle}
                                                    />
                                                </Col>
                                            </Row>

                                            {/* 수강권 연결 */}
                                            <Col style={{ gap: 8 }}>
                                                <Row style={{ alignItems: 'center', justifyContent: 'space-between' }}>
                                                    <Col style={{ gap: 2 }}>
                                                        <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>
                                                            사용 가능 수강권
                                                        </span>
                                                        <span style={{ fontSize: 11, color: '#9CA3AF' }}>
                                                            선택된 수강권을 가진 회원만 이 수업을 예약할 수 있어요 · 눌러서 추가/해제
                                                        </span>
                                                    </Col>
                                                    <span style={{
                                                        fontSize: 12, fontWeight: 600,
                                                        padding: '3px 10px', borderRadius: 12,
                                                        backgroundColor: editTicketIds.length > 0 ? '#ECFDF5' : '#FEF3C7',
                                                        color: editTicketIds.length > 0 ? '#065F46' : '#92400E',
                                                    }}>
                                                        {editTicketIds.length}개 선택됨
                                                    </span>
                                                </Row>

                                                {editTicketIds.length === 0 && (
                                                    <span style={{
                                                        fontSize: 12, color: '#92400E',
                                                        backgroundColor: '#FEF3C7', border: '1px solid #FDE68A',
                                                        padding: '6px 10px', borderRadius: 6,
                                                    }}>
                                                        ⚠ 선택된 수강권이 없으면 아무도 이 수업을 예약할 수 없어요
                                                    </span>
                                                )}

                                                {(ticketProducts as any[]).length === 0 ? (
                                                    <span style={{ fontSize: 13, color: '#9CA3AF', padding: '8px 0' }}>
                                                        등록된 수강권이 없습니다. 먼저 수강권을 등록해주세요.
                                                    </span>
                                                ) : (
                                                    <Row style={{ gap: 6, flexWrap: 'wrap' }}>
                                                        {(ticketProducts as any[]).map((t) => {
                                                            const sel = editTicketIds.includes(t._id);
                                                            const inactive = !t.isActive;
                                                            return (
                                                                <button key={t._id}
                                                                    onClick={() => {
                                                                        setEditTicketIds(sel ? editTicketIds.filter((id) => id !== t._id) : [...editTicketIds, t._id]);
                                                                        markDirty();
                                                                    }}
                                                                    style={{
                                                                        padding: '6px 12px', borderRadius: 18,
                                                                        fontSize: 12, fontWeight: sel ? 700 : 500,
                                                                        cursor: 'pointer',
                                                                        display: 'inline-flex', alignItems: 'center', gap: 5,
                                                                        transition: 'all 0.15s',
                                                                        border: sel ? '1.5px solid #10B981' : '1.5px dashed #D1D5DB',
                                                                        backgroundColor: sel ? '#10B981' : 'white',
                                                                        color: sel ? 'white' : '#9CA3AF',
                                                                        boxShadow: sel ? '0 1px 2px rgba(16,185,129,0.25)' : 'none',
                                                                        opacity: inactive ? 0.65 : 1,
                                                                    }}>
                                                                    {sel && (
                                                                        <svg width="11" height="11" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                                                                            <path d="M2 7.5L5.5 11L12 3.5" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                                                                        </svg>
                                                                    )}
                                                                    {!sel && <span style={{ fontSize: 13, lineHeight: 1 }}>+</span>}
                                                                    {t.name}
                                                                    {inactive && (
                                                                        <span style={{
                                                                            fontSize: 10, fontWeight: 600,
                                                                            padding: '1px 5px', borderRadius: 4,
                                                                            backgroundColor: sel ? 'rgba(255,255,255,0.25)' : '#F3F4F6',
                                                                            color: sel ? 'white' : '#6B7280',
                                                                        }}>
                                                                            비활성
                                                                        </span>
                                                                    )}
                                                                </button>
                                                            );
                                                        })}
                                                    </Row>
                                                )}
                                            </Col>

                                            {/* 강사 */}
                                            <Col style={{ gap: 4 }}>
                                                <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>기본 강사</span>
                                                <select value={editCoachId} onChange={(e) => { setEditCoachId(e.target.value); markDirty(); }} style={inputStyle}>
                                                    {/* 기본 강사를 지우는 저장은 없다(빈 값은 보내지 않는다) — 선택지로 두지 않는다 */}
                                                    {!editCoachId && <option value="" disabled>기본 강사 없음 · 선택</option>}
                                                    {editCoachId && !coachList.some((x) => x.code === editCoachId) && (
                                                        <option value={editCoachId}>{c.defaultCoachName || '현재 강사'}</option>
                                                    )}
                                                    {coachList.map((x) => <option key={x.code} value={x.code}>{x.value}</option>)}
                                                </select>
                                            </Col>

                                            {/* 정원 */}
                                            <Row style={{ gap: 12 }}>
                                                <Col style={{ flex: 1, gap: 4 }}>
                                                    <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>최대 정원</span>
                                                    <input type="number" value={editMaxCapacity} onChange={(e) => { setEditMaxCapacity(Number(e.target.value)); markDirty(); }} min={1} style={{ ...inputStyle, borderColor: editMaxCapacity < 1 ? '#EF4444' : undefined }} />
                                                    {editMaxCapacity < 1 && <span style={{ fontSize: 11, color: '#EF4444' }}>1명 이상</span>}
                                                </Col>
                                                <Col style={{ flex: 1, gap: 4 }}>
                                                    <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>최소 인원</span>
                                                    <input type="number" value={editMinCapacity} onChange={(e) => { setEditMinCapacity(Number(e.target.value)); markDirty(); }} min={0} style={{ ...inputStyle, borderColor: editMinCapacity > editMaxCapacity ? '#EF4444' : undefined }} />
                                                    {editMinCapacity > editMaxCapacity && <span style={{ fontSize: 11, color: '#EF4444' }}>최대 정원 초과</span>}
                                                </Col>
                                                <Col style={{ flex: 1, gap: 4 }}>
                                                    <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>대기 인원</span>
                                                    <input type="number" value={editWaitlistLimit} onChange={(e) => { setEditWaitlistLimit(Number(e.target.value)); markDirty(); }} min={0} style={inputStyle} />
                                                </Col>
                                            </Row>

                                            {/* 장소 */}
                                            <Col style={{ gap: 4 }}>
                                                <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>장소</span>
                                                <input value={editRoomId} onChange={(e) => { setEditRoomId(e.target.value); markDirty(); }} placeholder="장소" style={inputStyle} />
                                            </Col>

                                            {/* 예약 규칙 — 저장하면 예정 수업과 이미 예약한 회원에게도 바로 적용된다(서버가 회차에 반영) */}
                                            <GroupRuleFields
                                                value={editRules}
                                                centerRules={centerRules}
                                                onChange={(next) => { setEditRules(next); markDirty(); }}
                                                inputStyle={inputStyle}
                                            />
                                            {Object.keys(diffRuleFields(editRules)).length > 0 && (c.futureSessionCount ?? 0) > 0 && (
                                                <span style={{ fontSize: 12, color: '#B45309', lineHeight: 1.6, padding: '8px 12px', backgroundColor: '#FFFBEB', borderRadius: 8, border: '1px solid #FDE68A' }}>
                                                    저장하면 예정된 수업 {c.futureSessionCount}개와 이미 예약한 회원에게도 바로 적용돼요.
                                                    {centerRules?.autoClosingEnabled !== false && '폐강 기준을 늘리면 최소 인원이 안 찬 수업이 곧바로 폐강될 수 있어요.'}
                                                </span>
                                            )}

                                            {/* 예정 수업에도 적용 — 미래 ACTIVE 세션이 있을 때만 노출 */}
                                            {(c.futureSessionCount ?? 0) > 0 && (
                                                <Col style={{ gap: 4, padding: '10px 12px', backgroundColor: '#F0F9FF', borderRadius: 8, border: '1px solid #BAE6FD' }}>
                                                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#0C4A6E' }}>
                                                        <input type="checkbox" checked={applyToFuture} onChange={(e) => { setApplyToFuture(e.target.checked); markDirty(); }} />
                                                        이미 예정된 수업 {c.futureSessionCount}개에도 신청 가능 수강권 적용
                                                    </label>
                                                    <span style={{ fontSize: 11, color: '#0369A1', paddingLeft: 24 }}>
                                                        {applyToFuture
                                                            ? `저장하면 앞으로 만들 수업 + 예정된 ${c.futureSessionCount}개 수업의 ‘신청 가능 수강권’이 함께 바뀌어요.`
                                                            : `끄면 앞으로 새로 만드는 수업에만 적용되고, 예정된 ${c.futureSessionCount}개 수업은 지금 그대로예요.`}
                                                    </span>
                                                </Col>
                                            )}

                                            {/* 버튼 */}
                                            <Row style={{ gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
                                                <ActionButton variant="danger" onClick={() => setDeleteTarget(c)}>삭제</ActionButton>
                                                <ActionButton variant="primary" onClick={() => handleSave(c)} disabled={updateMutation.isLoading}>저장</ActionButton>
                                            </Row>
                                        </Col>
                                    )}
                                </Col>
                            );
                        })
                    )}
                </Col>
            </Col>

            {isCreateModal && (
                <GroupSessionCreateModal onClose={() => setIsCreateModal(false)} createAlert={createAlert} />
            )}

            {deleteTarget && (
                <Modal setIsVisible={() => setDeleteTarget(null)}>
                    <Col style={{ padding: 32, alignItems: 'center', gap: 16 }}>
                        <span style={{ fontSize: 16, fontWeight: 600, color: '#374151' }}>수업 삭제</span>
                        <span style={{ fontSize: 14, color: '#6B7280', textAlign: 'center' }}><b>{deleteTarget.title}</b>을 삭제하시겠습니까?</span>
                        <Row style={{ gap: 8 }}>
                            <ActionButton variant="neutral" onClick={() => setDeleteTarget(null)}>취소</ActionButton>
                            <ActionButton variant="danger" onClick={handleDelete} disabled={deleteMutation.isLoading}>삭제</ActionButton>
                        </Row>
                    </Col>
                </Modal>
            )}
        </Col>
    );
};

/* ═══════ 통일 스타일 액션 버튼 ═══════ */
type ActionVariant = 'primary' | 'neutral' | 'danger';
const ACTION_VARIANTS: Record<ActionVariant, { bg: string; color: string; border: string; hoverBg: string }> = {
    primary: { bg: '#3B82F6', color: 'white', border: '1px solid #3B82F6', hoverBg: '#2563EB' },
    neutral: { bg: 'white', color: '#374151', border: '1px solid #D1D5DB', hoverBg: '#F9FAFB' },
    danger: { bg: 'white', color: '#DC2626', border: '1px solid #FCA5A5', hoverBg: '#FEF2F2' },
};

const ActionButton = ({
    variant, onClick, disabled, children,
}: {
    variant: ActionVariant;
    onClick?: () => void;
    disabled?: boolean;
    children: React.ReactNode;
}) => {
    const v = ACTION_VARIANTS[variant];
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            onMouseEnter={(e) => { if (!disabled) e.currentTarget.style.backgroundColor = v.hoverBg; }}
            onMouseLeave={(e) => { if (!disabled) e.currentTarget.style.backgroundColor = v.bg; }}
            style={{
                height: 36, padding: '0 16px', minWidth: 72,
                borderRadius: 8, fontSize: 13, fontWeight: 600,
                backgroundColor: v.bg, color: v.color, border: v.border,
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.5 : 1,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                transition: 'background-color 0.15s',
            }}
        >
            {children}
        </button>
    );
};

export default GroupClassManagePage;
